import React from 'react';
import MoneyComponent from 'react/elements/MoneyComponent';
import CategoryColor from 'utilities/CategoryColor';
import getCategoryById from 'utilities/getCategoryById';
import LedgerCell from './LedgerCell';

const HEADINGS = {
	where: 'Item',
	transaction_date: 'Date',
	amount: 'Amount',
	category_id: 'Category',
};

const DATE_FORMAT = 'MM/DD/YYYY';

/**
 * The recordings half of the ledger -- the spreadsheet's E2:L18.
 *
 * Four columns and no controls: no edit button, no row menu, no delete affordance. A row is
 * changed by typing over it, which is the whole reason this screen exists next to a Transactions
 * tab that can already do all of that with buttons. Anything that needs a menu belongs there.
 *
 * The last row is always blank and always present. Committing into it is what creates a
 * transaction, so the entry loop never has to reach for "add".
 *
 * @param selectedDates {Object} the months on screen -- what the category picker may offer is
 *                                scoped to them, see `availableCategoriesForMonths()`
 * @param commit {Function} `(row, field, value)` -- the page decides what a field change means
 */
export default function LedgerRecordingsBlock({ pane, paneIndex, selectedDates, commit }) {
	const rowsIn = (section) => pane.rows.filter((row) => row.section === section);

	const cellProps = (row, cellIndex) => ({
		key: row.cells[cellIndex].key,
		paneIndex,
		row,
		cellIndex,
	});

	const edit = (row, field, type, value, cleared) => ({
		type,
		value,
		commit: (committed) => commit(row, field, committed),
		clear: cleared === undefined ? null : () => commit(row, field, cleared),
	});

	function renderEntryRow(row) {
		const { transaction } = row;
		const category = transaction.category_id ? getCategoryById(transaction.category_id) : null;
		return (
			<tr key={row.key} className={row.kind === 'new' ? 'new_row' : ''}>
				<LedgerCell
					{...cellProps(row, 0)}
					className='item'
					title={transaction.where || ''}
					edit={edit(row, 'where', 'text', transaction.where || '', '')}
				>
					{transaction.where}
				</LedgerCell>
				<LedgerCell
					{...cellProps(row, 1)}
					className='date'
					edit={edit(row, 'transaction_date', 'date', transaction.transaction_date)}
				>
					{transaction.transaction_date ? transaction.transaction_date.format(DATE_FORMAT) : ''}
				</LedgerCell>
				<LedgerCell
					{...cellProps(row, 2)}
					className='amount'
					edit={edit(row, 'amount', 'money', transaction.amount, 0)}
				>
					{transaction.amount === null || transaction.amount === undefined
						? '' : <MoneyComponent amount={transaction.amount} />}
				</LedgerCell>
				<LedgerCell
					{...cellProps(row, 3)}
					className='category'
					title={category ? category.fullName() : ''}
					style={category ? {
						backgroundColor: category.getFillColor(),
						color: CategoryColor.FILL_TEXT_COLOR,
					} : undefined}
					edit={{ ...edit(row, 'category_id', 'category', transaction.category_id, null), selectedDates }}
				>
					{category ? category.fullName() : ''}
				</LedgerCell>
			</tr>
		);
	}

	function renderRow(row) {
		switch(row.kind) {
			case 'header':
				return (
					<tr key={row.key}>
						{row.cells.map((cell, cellIndex) => (
							<LedgerCell
								{...cellProps(row, cellIndex)}
								tag='th'
								className={cell.key === 'amount' ? 'amount' : cell.key}
							>
								{HEADINGS[cell.key]}
							</LedgerCell>
						))}
					</tr>
				);
			case 'transaction':
			case 'new':
				return renderEntryRow(row);
			case 'total':
				return (
					<tr key={row.key} className='total'>
						<LedgerCell {...cellProps(row, 0)} tag='th' className='item'>Total</LedgerCell>
						<LedgerCell {...cellProps(row, 1)} className='amount'><MoneyComponent amount={row.total} /></LedgerCell>
						<LedgerCell {...cellProps(row, 2)} className='category' />
					</tr>
				);
			default:
				return null;
		}
	}

	return (
		<table className='ledger_recordings_table'>
			<thead>{rowsIn('head').map(renderRow)}</thead>
			<tbody>{rowsIn('body').map(renderRow)}</tbody>
			<tfoot>{rowsIn('foot').map(renderRow)}</tfoot>
		</table>
	);
}
